import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Tag } from 'lucide-react';
import UseAxiosPublic from '../hooks/UseAxiosPublic';
import SEO from '../components/SEO';

const BlogPublic = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const axiosPublic = UseAxiosPublic();

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await axiosPublic.get('/blogs_list');
                if (response.data.success) {
                    setBlogs(response.data.data);
                }
            } catch (err) {
                console.error('Error fetching blogs:', err);
                setError('Failed to load blog posts.');
            } finally {
                setLoading(false);
            }
        };

        fetchBlogs();
    }, [axiosPublic]);

    return (
        <div className="min-h-screen bg-slate-950 pt-28 pb-16 px-4 sm:px-8 lg:px-16 text-slate-300 font-sans">
            {/* Dynamic SEO Tags */}
            <SEO 
                title="Restaurant Insights & Operational Tips"
                description="Explore industry articles, POS setup guides, multi-branch management tips, and operational insights for modern restaurants."
                keywords={['restaurant blog', 'restaurant pos insights', 'getsoko news', 'restaurant management tips']}
                url="https://getsoko.app/blog"
            />

            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <span className="text-xs font-black uppercase tracking-widest text-blue-500 bg-blue-500/10 px-4 py-1.5 rounded-full border border-blue-500/20">
                        Official Getsoko Blog
                    </span>
                    <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mt-4 mb-4">
                        Insights & Resources for <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                            Modern Restaurants
                        </span>
                    </h1>
                    <p className="text-slate-400 text-base sm:text-lg">
                        Stay ahead with top strategies on inventory management, branch expansion, POS technology, and staff operations.
                    </p>
                </div>

                {/* Content States */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[1, 2, 3].map((n) => (
                            <div key={n} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 animate-pulse">
                                <div className="h-48 bg-slate-800 rounded-xl mb-4"></div>
                                <div className="h-6 bg-slate-800 rounded w-3/4 mb-2"></div>
                                <div className="h-4 bg-slate-800 rounded w-full mb-4"></div>
                                <div className="h-10 bg-slate-800 rounded-xl"></div>
                            </div>
                        ))}
                    </div>
                ) : error ? (
                    <div className="text-center text-red-400 py-12 bg-slate-900/40 rounded-2xl border border-red-500/20">
                        {error}
                    </div>
                ) : blogs.length === 0 ? (
                    <div className="text-center text-slate-400 py-16 bg-slate-900/40 rounded-2xl border border-slate-800">
                        <p className="text-lg font-bold text-white mb-1">No articles published yet.</p>
                        <p className="text-sm">Check back soon for latest insights!</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {blogs.map((blog) => (
                            <article 
                                key={blog._id} 
                                className="bg-slate-900/50 border border-slate-800/80 hover:border-blue-500/50 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 shadow-xl flex flex-col"
                            >
                                {/* Thumbnail Image */}
                                <div className="h-48 w-full overflow-hidden relative">
                                    <img 
                                        src={blog.thumbnail} 
                                        alt={blog.title} 
                                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-80"></div>
                                </div>

                                {/* Content Body */}
                                <div className="p-6 flex-1 flex flex-col justify-between">
                                    <div>
                                        {/* Keywords / Tags */}
                                        {blog.keywords && blog.keywords.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mb-3">
                                                {blog.keywords.slice(0, 2).map((kw, i) => (
                                                    <span key={i} className="text-[10px] font-bold text-blue-400 bg-blue-500/10 px-2.5 py-0.5 rounded-md border border-blue-500/20 flex items-center gap-1">
                                                        <Tag size={10} />
                                                        {kw}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <h2 className="text-xl font-bold text-white mb-2 line-clamp-2 hover:text-blue-400 transition-colors">
                                            <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                                        </h2>

                                        <p className="text-slate-400 text-sm mb-4 line-clamp-3">
                                            {blog.metaDescription}
                                        </p>
                                    </div>

                                    {/* Footer Info */}
                                    <div className="pt-4 border-t border-slate-800/60 flex items-center justify-between text-xs text-slate-500">
                                        <div className="flex items-center gap-4">
                                            <span className="flex items-center gap-1">
                                                <User size={14} className="text-slate-400" />
                                                {blog.author || 'Getsoko Team'}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Calendar size={14} className="text-slate-400" />
                                                {new Date(blog.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>

                                        <Link 
                                            to={`/blog/${blog.slug}`}
                                            className="text-blue-400 hover:text-blue-300 font-bold flex items-center gap-1 group"
                                        >
                                            Read 
                                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                                        </Link>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogPublic;